import { printDebug } from '@/lib/printDebug'
import { isBhooklyNativeServerApp, nativeAgentVersion, versionAtLeast } from '@/lib/bhooklyNative'

/**
 * Print outcomes coming BACK from the Android agent (F-AND-N1).
 *
 * Until agent 1.4.0 the bridge was one-way. `printJobs()` returned void, the
 * agent worked out exactly which slips printed and which failed, spent that on
 * one three-second toast, and the POS was never told. So it could not retry,
 * warn, log or fall back — and Mizaj's outage on 2026-09-10 had to be
 * diagnosed from a staff photograph of that toast
 * (audit/MIZAJ_TABLET_PRINT_ECONNREFUSED_2026-09-10.md).
 *
 * The shell now injects `window.__bhooklyPrintResult(json)` after each batch.
 * This module owns that entry point and hands each result to whoever is
 * waiting on it.
 *
 * THE RULE FOR EVERY CALLER: a result may never arrive. The APK does not
 * auto-install — each tablet is updated by hand — so an agent below 1.4.0 is a
 * normal, indefinite state in the field. Nothing may block on a callback, and
 * "no result" must always mean "unknown", never "failed".
 */

/** First agent that reports outcomes back to the page. */
export const MIN_AGENT_WITH_PRINT_RESULT = '1.4.0'

export interface PrintJobOutcome {
  station: string
  host: string
  port: number
  ok: boolean
  error: string | null
}

export interface PrintBatchResult {
  /** The agent reported back. False means we are simply blind, not that it failed. */
  supported: boolean
  /** The agent claimed support but nothing arrived in time. Also "unknown". */
  timedOut: boolean
  printed: number
  failed: number
  jobs: PrintJobOutcome[]
}

/** A batch we are blind to — an old agent, or no shell at all. */
export const UNKNOWN_RESULT: PrintBatchResult = {
  supported: false,
  timedOut: false,
  printed: 0,
  failed: 0,
  jobs: [],
}

declare global {
  interface Window {
    __bhooklyPrintResult?: (json: string) => void
  }
}

/**
 * Longest we will wait for a batch.
 *
 * The agent's own ceiling is roughly its connect budget per job, and a fire can
 * carry several stations. Generous on purpose: a late result is still useful,
 * whereas timing out early would report a healthy printer as unknown and train
 * staff to ignore the warning.
 */
const RESULT_TIMEOUT_MS = 45_000

type Waiter = {
  resolve: (r: PrintBatchResult) => void
  timer: ReturnType<typeof setTimeout>
}

const waiters = new Map<string, Waiter>()
let installed = false
let seq = 0

// Moved to bhooklyNative (the rider tracker needs it too); re-exported for callers.
export { versionAtLeast }

/** True when this device's agent reports print outcomes back to the page. */
export function agentReportsPrintResults(): boolean {
  if (!isBhooklyNativeServerApp()) return false
  return versionAtLeast(nativeAgentVersion(), MIN_AGENT_WITH_PRINT_RESULT)
}

function parseResult(json: string): { id: string; result: PrintBatchResult } | null {
  try {
    const doc = JSON.parse(json) as Record<string, unknown>
    const rawJobs = Array.isArray(doc.jobs) ? doc.jobs : []
    const jobs: PrintJobOutcome[] = rawJobs.map((j) => {
      const o = (j ?? {}) as Record<string, unknown>
      return {
        station: typeof o.station === 'string' ? o.station : '',
        host: typeof o.host === 'string' ? o.host : '',
        port: typeof o.port === 'number' ? o.port : 0,
        ok: o.ok === true,
        error: typeof o.error === 'string' ? o.error : null,
      }
    })
    return {
      id: typeof doc.id === 'string' ? doc.id : '',
      result: {
        supported: true,
        timedOut: false,
        printed: jobs.filter((j) => j.ok).length,
        failed: jobs.filter((j) => !j.ok).length,
        jobs,
      },
    }
  } catch (e) {
    printDebug.warn('bridge', 'unreadable print result from the agent', e)
    return null
  }
}

/**
 * Install the entry point the Android shell calls. Idempotent, and safe to call
 * on a desktop browser where it simply never fires.
 */
export function installPrintResultChannel(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  window.__bhooklyPrintResult = (json: string) => {
    const parsed = parseResult(json)
    if (!parsed) return
    const { id, result } = parsed

    printDebug.log('bridge', `print result (${result.printed} ok, ${result.failed} failed)`, {
      jobs: result.jobs.map((j) => `${j.station || '(unnamed)'}@${j.host}:${j.port} ${j.ok ? 'ok' : j.error}`),
    })

    const waiter = waiters.get(id)
    if (!waiter) {
      // An unmatched result is normal, not an error: a batch whose caller did
      // not ask to be told, or one that already timed out.
      return
    }
    waiters.delete(id)
    clearTimeout(waiter.timer)
    waiter.resolve(result)
  }
}

/** A fresh correlation id for one batch. */
export function nextPrintBatchId(): string {
  seq += 1
  return `p${seq}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Wait for one batch's outcome. NEVER rejects, and never blocks indefinitely.
 *
 * Resolves to UNKNOWN_RESULT when the agent does not report back, so callers
 * can treat "supported === false" and "timedOut === true" identically: we do
 * not know, so say nothing rather than accuse a working printer.
 */
export function awaitPrintResult(id: string, timeoutMs = RESULT_TIMEOUT_MS): Promise<PrintBatchResult> {
  if (!agentReportsPrintResults()) return Promise.resolve(UNKNOWN_RESULT)
  installPrintResultChannel()
  return new Promise<PrintBatchResult>((resolve) => {
    const timer = setTimeout(() => {
      waiters.delete(id)
      printDebug.warn('bridge', `no print result for batch ${id} after ${timeoutMs}ms`)
      resolve({ ...UNKNOWN_RESULT, supported: true, timedOut: true })
    }, timeoutMs)
    waiters.set(id, { resolve, timer })
  })
}

/**
 * Turn one failed job into something a cashier can act on.
 *
 * The agent forwards Android's raw text, e.g. "failed to connect to
 * /192.168.1.10 (port 9100) from /192.168.1.3 (port 33312) after 5000ms:
 * isConnected failed: ECONNREFUSED (Connection refused)". The errno in there is
 * the entire diagnosis and it points at three different fixes, but nobody on a
 * restaurant floor is going to read it. So say which one this is.
 *
 * Note "after 5000ms" is NOT a measurement — it is the connect timeout echoed
 * back — so never present it as one.
 */
export function describePrintFailure(job: PrintJobOutcome): string {
  const where = job.host ? `${job.host}:${job.port}` : 'the printer'
  const raw = job.error ?? ''

  if (/no printer set/i.test(raw)) {
    return `No printer address is set for ${job.station || 'this station'}.`
  }
  if (/ECONNREFUSED/i.test(raw)) {
    // A refusal means a live host sent a reset: the address is reachable but
    // nothing is listening. Usually a stuck print server, or that address now
    // belongs to a different device.
    return `${where} is on the network but refused the print. Switch the printer off and on, then try again.`
  }
  if (/EHOSTUNREACH|ENETUNREACH|no route/i.test(raw)) {
    return `Nothing is at ${where} on this network. Check the address and that this device is on the right Wi-Fi.`
  }
  if (/ETIMEDOUT|SocketTimeout|timed out/i.test(raw)) {
    return `${where} did not answer. Check the printer is switched on and connected.`
  }
  if (/EPIPE|ECONNRESET|broken pipe/i.test(raw)) {
    return `${where} dropped the connection mid-print. Check the paper and the cable, then try again.`
  }
  return raw ? `${where}: ${raw}` : `${where} could not be reached.`
}

/** Test seam: drop any pending waiters between cases. */
export function __resetPrintResultChannelForTests(): void {
  waiters.forEach((w) => clearTimeout(w.timer))
  waiters.clear()
  installed = false
  seq = 0
  if (typeof window !== 'undefined') delete window.__bhooklyPrintResult
}
